import { nextBoundary, scheduleWindow } from './sequencer.js';
import { cues, eras, stingers } from './themes.js';
import { createRenderer } from './renderer.js';

const LOOK_AHEAD = 0.12;
const TICK_MS = 25;
const has = (table, key) => Object.hasOwn(table, key);

export function createMusicDirector({ context, destination } = {}) {
  if (!context || context.state === 'closed') throw new TypeError('Pass an open, gesture-unlocked AudioContext');
  const renderer = createRenderer(context, destination ?? context.destination);
  let active = null;
  let pending = null;
  let intensity = 0.4;
  let era = eras.occupied;
  let disposed = false;
  let lastStinger = -Infinity;
  const fading = new Set();

  function begin(id, origin, fade = 0.15) {
    const pattern = cues[id];
    const channel = renderer.createChannel(pattern, origin, fade);
    renderer.mix(channel, intensity, era, context.currentTime);
    return { id, pattern, channel, origin, cursor: origin };
  }

  function tick() {
    if (disposed || context.state !== 'running') return;
    const now = context.currentTime;
    const end = now + LOOK_AHEAD;
    renderer.collect(now);
    for (const track of fading) if (track.channel.stopAt <= now) fading.delete(track);
    if (pending && pending.at < end) {
      // Rebase a missed transition after background throttling; never replay a backlog.
      const at = pending.at < now ? nextBoundary(now + LOOK_AHEAD, active.origin, active.pattern.bpm) : pending.at;
      pending.at = at;
      if (at < end) {
        const duration = 60 / active.pattern.bpm;
        renderer.fadeOut(active.channel, at, duration);
        fading.add(active);
        active = begin(pending.id, at, duration);
        pending = null;
      }
    }
    for (const track of [...fading, ...(active ? [active] : [])]) {
      const from = Math.max(track.cursor, now);
      const to = Math.min(end, track.channel.stopAt);
      if (to <= from) continue;
      for (const event of scheduleWindow(track.pattern, { from, to, origin: track.origin })) renderer.schedule(track.channel, event);
      track.cursor = to;
    }
  }

  const timer = setInterval(tick, TICK_MS);
  return {
    setCue(id) {
      if (disposed) return;
      if (!has(cues, id)) throw new RangeError(`Unknown music cue: ${id}`);
      if (active?.id === id) { pending = null; return; }
      if (pending?.id === id) return;
      if (!active) {
        active = begin(id, context.currentTime + 0.03);
        tick();
      } else {
        pending = { id, at: nextBoundary(context.currentTime + LOOK_AHEAD + 0.01, active.origin, active.pattern.bpm, active.pattern.beatsPerBar) };
      }
    },
    setIntensity(value) {
      if (disposed) return;
      if (!Number.isFinite(value)) throw new TypeError('Intensity must be finite');
      intensity = Math.min(1, Math.max(0, value));
      if (active) renderer.mix(active.channel, intensity, era);
      for (const track of fading) renderer.mix(track.channel, intensity, era);
    },
    setEra(id) {
      if (disposed) return;
      if (!has(eras, id)) throw new RangeError(`Unknown music era: ${id}`);
      era = eras[id];
      if (active) renderer.mix(active.channel, intensity, era);
      for (const track of fading) renderer.mix(track.channel, intensity, era);
    },
    stinger(id) {
      if (disposed) return;
      if (!has(stingers, id)) throw new RangeError(`Unknown music stinger: ${id}`);
      const now = context.currentTime;
      if (context.state !== 'running' || now - lastStinger < 0.2) return;
      lastStinger = now;
      const pattern = stingers[id];
      const origin = now + 0.01;
      const channel = renderer.createChannel(pattern, origin, 0.008);
      const end = origin + pattern.beats * 60 / pattern.bpm;
      for (const event of scheduleWindow(pattern, { from: origin, to: end, origin, loop: false })) renderer.schedule(channel, event);
      renderer.fadeOut(channel, end, 0.3);
    },
    setMuted(muted) { if (!disposed) renderer.setMuted(Boolean(muted)); },
    dispose() {
      if (disposed) return;
      disposed = true;
      clearInterval(timer);
      pending = null;
      active = null;
      fading.clear();
      renderer.dispose();
    },
  };
}
